export type BrainSource = "tik" | "you" | "playmath";

/** Unselected icon opacity (selected is always 1). */
export const VALUE_PROP_DIMMED_OPACITY = 0.34;
/** Unselected icon scale (selected is always 1). */
export const VALUE_PROP_DIMMED_SCALE = 0.82;
/** Hover/focus opacity for unselected icons. */
export const VALUE_PROP_HOVER_OPACITY = 0.78;
/** Hover/focus scale for unselected icons. */
export const VALUE_PROP_HOVER_SCALE = 0.92;
/** Rock amplitude for unselected icons (degrees each side of base tilt). */
export const VALUE_PROP_ICON_ROCK_IDLE_DEG = 3;
/** Rock amplitude for the selected icon (degrees each side of base tilt). */
export const VALUE_PROP_ICON_ROCK_SELECTED_DEG = 9;
/** One full back-and-forth rock period (seconds). */
export const VALUE_PROP_ICON_ROCK_PERIOD_S = 3.2;
/**
 * Auto-rotate order around the center Rive (top-left → right → bottom-left).
 * Stops permanently after the user presses any icon.
 */
export const VALUE_PROP_CLOCKWISE_ORDER: readonly BrainSource[] = ["tik", "playmath", "you"];
/** How long each auto-selected icon stays active before advancing. */
export const VALUE_PROP_AUTO_ROTATE_MS = 3200;
/**
 * Demo-cursor “about to click” lead time before each auto-rotate.
 * Must equal APPROACH + HOVER + PRESS so the press lands on the rotate tick.
 */
export const VALUE_PROP_DEMO_CURSOR_LEAD_MS = 880;
/** Cursor pops in and settles over the next icon. */
export const VALUE_PROP_DEMO_APPROACH_MS = 400;
/** Brief pause after settle before the fake click. */
export const VALUE_PROP_DEMO_HOVER_MS = 320;
/** Cursor/icon depress for the fake click. */
export const VALUE_PROP_DEMO_PRESS_MS = 160;
/** Cursor fades out after the selection advances (overlaps next hold). */
export const VALUE_PROP_DEMO_RELEASE_MS = 280;
/** Phases for the auto-rotate affordance cursor. */
export type ValuePropDemoClickPhase = "idle" | "approach" | "press" | "release";
/** Quick springy scale-in duration for the active center Rive layer. */
export const VALUE_PROP_RIVE_POP_MS = 220;
/** Starting scale for inactive / about-to-pop Rive layers. */
export const VALUE_PROP_RIVE_POP_FROM_SCALE = 0.88;
/** Springy overshoot easing for the Rive pop (slight bounce). */
export const VALUE_PROP_RIVE_POP_EASING = "cubic-bezier(0.34, 1.55, 0.64, 1)";

export const BRAIN_SOURCES = [
  {
    id: "tik" as const,
    label: "TikTok",
    iconSrc: "/images/marketing/home/value/icon-tik.webp",
    riveSrc: "/images/marketing/home/value/slurp_derp1.riv",
    side: "left" as const,
    /** Stable “random” base tilt in degrees. */
    tiltDeg: -11,
  },
  {
    id: "you" as const,
    label: "YouTube",
    iconSrc: "/images/marketing/home/value/icon-you.webp",
    riveSrc: "/images/marketing/home/value/slurp_derp2.riv",
    side: "left" as const,
    tiltDeg: 14,
  },
  {
    id: "playmath" as const,
    label: "PlayMath.org",
    iconSrc: "/images/marketing/home/value/icon-playMath.webp",
    riveSrc: "/images/marketing/home/value/slurp_smart.riv",
    side: "right" as const,
    tiltDeg: -7,
  },
] as const;

export type BrainSourceEntry = (typeof BRAIN_SOURCES)[number];

export function riveSrcForBrainSource(source: BrainSource): string {
  const match = BRAIN_SOURCES.find((entry) => entry.id === source);
  if (!match) {
    console.error("[ValueProp] Unknown brain source; falling back to tik", { source });
    return BRAIN_SOURCES[0].riveSrc;
  }
  return match.riveSrc;
}

/** Next icon in clockwise auto-rotate order. */
export function nextClockwiseBrainSource(current: BrainSource): BrainSource {
  const idx = VALUE_PROP_CLOCKWISE_ORDER.indexOf(current);
  if (idx === -1) {
    console.error("[ValueProp] Unknown brain source in clockwise order; restarting at tik", {
      current,
    });
    return VALUE_PROP_CLOCKWISE_ORDER[0];
  }
  return VALUE_PROP_CLOCKWISE_ORDER[(idx + 1) % VALUE_PROP_CLOCKWISE_ORDER.length];
}
